import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { css } from 'styled-components'
import { isEqual } from 'lodash'
import CloseIcon from '@material-ui/icons/Close'
import { RedAsterisk } from 'components/common/styles'
import cn from 'classnames'
import moment from 'moment'
import { Collapse, IconButton } from '@material-ui/core'
import { List, Map } from 'immutable'
import { ReactComponent as SiteIcon } from 'assets/img/triggers-site-icon.svg'
import { ReactComponent as SchedulingIcon } from 'assets/img/triggers-scheduling-icon.svg'
import SelectInput from 'components/common/Inputs/SelectInput'
import { fetchSiteList } from 'reducers/logbookForm/reducer'
import { fetchWorkflowList, fetchWorkflowInfo } from 'reducers/myJobs/reducer'
import { fetchTriggerDefaultAssignments as fetchDefaultAssignments } from 'reducers/jobsTriggers/reducer'
import Psfs from 'lib/psfs/Psfs'
import { ReactComponent as InfoIcon } from 'assets/img/triggers-info-icon.svg'
import withPromise from 'helpers/withPromise'
import {
  StyledActionSection,
  StyledButtonHolder,
  StyledCondition,
  StyledModal,
  StyledModalBoxSection,
  StyledModalOverlay,
  StyledName,
  StyledRuleSettings,
  StyledTitle,
  StyledFormSection,
  StyledErrorMessageHolder,
} from 'components/common/Modals/styles'
import {
  setFormData,
  DEFAULT_FORM_DATA,
  createJob,
} from 'reducers/managementUI/reducer'
import StyledButton from 'components/common/Button/Button'
import {
  ASSIGNMENTS,
  END_DATE_AFTER_NUMBER,
  FIELD_DUE_DATE_DATE,
  WORKFLOW,
  FREQUENCY,
  FIELD_SITE,
  PARAMETER_ID,
  PARAMETER_TITLE,
  FIELD_UNITS,
  MAX_STOP_AFTER_TERM_VALUES,
  VALUE_DAYS,
  VALUE_WEEKS,
  VALUE_MONTHS,
  REGULAR_REPEAT_SCHEDULE,
  VALUE_CUSTOM,
  VALUE_OCCURRENCES,
  FIELD_DUE_DATE_TIME,
  START_TIME,
  FIELD_DUE_DATE,
  DAYS_OF_WEEK,
  WEEK_ORDINARY_NUMBER,
  DAY_OF_WEEK,
  DAY_OF_MONTH,
  VALUE_NEVER,
  PARAMETER_NAME,
  FIELD_START_DATE,
  REPEAT,
  REGULAR,
  END_DATE_ON_DATE,
  VALUE_AFTER,
} from 'constants/managementUI'
import { BACKEND_TIME_FORMAT, BACKEND_DATE_FORMAT } from 'constants/dateTime'
import { Site, Workflow } from 'types/common'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { BUTTON_TYPE_SECONDARY } from 'components/common/Button/constants'
import messages from '../translate'
import SiteAndUnitsTable from './SiteAndUnitsTable/SiteAndUnitsTable'
import { formatAssignees, formatList } from './PlanNewJob.utils'
import { PlanNewJobSidebar, ModalTitle } from './PlanNewJob.styles'
import Scheduling from './Scheduling/Scheduling'
import Assignment from './Assignment/AssignmentSection/AssignmentSection'
import AssignmentMenu from './Assignment/AssignmentSubMenu/AssignmentSubMenu'
import {
  AssignmentDataItem,
  PlanNewJobProps,
  LocusesAndGroups,
  SiteWithUnits,
  ValidationErrors,
} from './PlanNewJob.types'

// eslint-disable-next-line max-statements
const PlanNewJob = ({
  intl: { formatMessage },
  isSidebarOpened,
  toggleIsSidebarOpened,
  fetchWorkflowList,
  workflowList,
  setFormData,
  stateFormData,
  fetchSiteList,
  siteList,
  isFetching,
  fetchWorkflowInfo,
  fetchDefaultAssignments,
  defaultAssignments,
  featureFlags,
  workflowInfo,
  createJob,
  fetchManagementUITable,
  isPlanNewJobSaving,
}: PlanNewJobProps & WrappedComponentProps) => {
  const [isAssignmentCollapseOpened, setIsAssignmentCollapseOpened] = useState(
    false,
  )
  const [isAssignmentSubModalOpened, setIsAssignmentSubModalOpened] = useState(
    false,
  )
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [isSiteCollapseOpened, setIsSiteCollapseOpened] = useState(false)
  const [isSchedulingCollapseOpened, setIsSchedulingCollapseOpened] = useState(
    false,
  )

  const defaultAssignmentsList = defaultAssignments.toJS()

  const formData = stateFormData.toJS()
  const isDefaultAssignment = isEqual(
    defaultAssignmentsList,
    formData[ASSIGNMENTS],
  )

  const sites = siteList.toJS()
  const {
    workflow,
    repeat = '',
    startTime = '',
    dueDate = '',
    startDate = '',
    dueDateDate = '',
    dueDateTime = '',
    [REGULAR_REPEAT_SCHEDULE]: {
      frequency = '',
      repeatBy = '',
      endDateAfterNumber = '',
      endDateAfterTerm = '',
      endDate = '',
      daysOfWeek = '',
      dayOfWeek = '',
      weekOrdinalNumber = '',
      daysOfMonth = [],
      endDateOnDate = '',
    } = {},
  } = formData
  const dueDateTimeIsValid = moment(dueDateTime).isValid()

  useEffect(() => {
    if (isSidebarOpened) {
      fetchWorkflowList()
      setFormData({
        ...formData,
        [START_TIME]: new Date(),
        [FIELD_START_DATE]: new Date(),
      })
    } else {
      setValidationErrors({})
      setFormData(DEFAULT_FORM_DATA)
      setIsSiteCollapseOpened(false)
      setIsSchedulingCollapseOpened(false)
      setIsAssignmentCollapseOpened(false)
    }
  }, [isSidebarOpened])

  useEffect(() => {
    if (workflow) {
      fetchDefaultAssignments({
        triggeredJobWorkflowId: workflow,
        siteId: null,
        shouldSetDefaultAssignmentsToForm: true,
      })
    }
  }, [workflow])

  useEffect(() => {
    setFormData({
      ...formData,
      [ASSIGNMENTS]: !formData.sitesAndUnits.length
        ? defaultAssignmentsList
        : defaultAssignmentsList?.filter((assignment: AssignmentDataItem) =>
            formData.sitesAndUnits.some(
              ({ site }: { site: number }) => site === assignment.site.id,
            ),
          ),
    })
  }, [
    JSON.stringify(defaultAssignmentsList),
    JSON.stringify(formData.sitesAndUnits),
  ])

  useEffect(() => {
    if (workflow) {
      const psfs = new Psfs()
      const filters = [workflow]

      psfs
        .addFilter('active', 1)
        .addFilter('visibleInUi', 1)
        .addFilter('workflow', filters, 'in')
        .addFields([PARAMETER_ID, PARAMETER_NAME])

      withPromise(fetchWorkflowInfo, {
        id: workflow,
      }).then(workflowResponse => {
        if (Object.prototype.hasOwnProperty.call(workflowResponse, 'error')) {
          return Promise.resolve(workflowResponse)
        }
      })

      withPromise(fetchSiteList, { psfs })
    }
  }, [workflow])

  const workflows: Workflow[] = workflowList.toJS()

  const buildAutomaticDueDateProperties = (id: number) => {
    const defaultProperties = {
      automaticDueDate: false,
      formData: {
        [FIELD_DUE_DATE]: VALUE_NEVER,
        [FIELD_DUE_DATE_DATE]: null,
        [FIELD_DUE_DATE_TIME]: null,
      },
    }
    const selectedWorkflow = workflows.find(w => w.id === id)
    if (!selectedWorkflow) {
      return defaultProperties
    }
    const {
      automatic_due_date: automaticDueDate,
      automatic_due_date_days: automaticDueDateDays,
      automatic_due_date_time: automaticDueDateTime,
    } = selectedWorkflow
    if (!automaticDueDate) {
      return defaultProperties
    }
    const [hours, minutes, seconds] = automaticDueDateTime
      .split(':')
      .map((x: string) => parseInt(x, 10))
    const startDateTime = moment()
    const endDateTime = startDateTime
      .clone()
      .add(automaticDueDateDays, 'day')
      .set('hours', hours)
      .set('minutes', minutes)
      .set('seconds', seconds)

    return {
      automaticDueDate,
      formData: {
        [FIELD_DUE_DATE]: VALUE_CUSTOM,
        [FIELD_DUE_DATE_DATE]: endDateTime,
        [FIELD_DUE_DATE_TIME]: endDateTime,
      },
    }
  }

  const onChangeWorkflow = (value: string) => {
    const newValidationErrors = { ...validationErrors }

    delete newValidationErrors[WORKFLOW]

    setValidationErrors(newValidationErrors)
    const automaticDueDateProperties = buildAutomaticDueDateProperties(
      parseInt(value, 10),
    )

    setFormData({
      ...formData,
      [WORKFLOW]: value,
      [FIELD_SITE]: [],
      ...automaticDueDateProperties.formData,
    })

    setIsSiteCollapseOpened(true)
    setIsSchedulingCollapseOpened(automaticDueDateProperties.automaticDueDate)
  }

  const onChangeFormValue = (
    value: string | number | string[] | MaterialUiPickersDate | SiteWithUnits[],
    field: string,
  ) => {
    setFormData({
      ...formData,
      [field]: value,
    })
  }

  const { needs_locus, needs_locus_group } = useMemo(
    () => workflowInfo.toJS(),
    [JSON.stringify(workflowInfo)],
  )

  const workflowValidationErrorCondition = !workflow
  const sitesValidationErrorCondition = !formData.sitesAndUnits.length
  const unitsValidationErrorCondition =
    (needs_locus || needs_locus_group) &&
    formData.sitesAndUnits.some(
      ({
        site,
        locusesAndGroups,
      }: {
        site: number
        locusesAndGroups: LocusesAndGroups[]
      }) => site && locusesAndGroups.length === 0,
    )

  const stopAfterNumberValidationErrorCondition =
    (endDateAfterTerm === VALUE_DAYS &&
      endDateAfterNumber > MAX_STOP_AFTER_TERM_VALUES.DAYS) ||
    (endDateAfterTerm === VALUE_WEEKS &&
      endDateAfterNumber > MAX_STOP_AFTER_TERM_VALUES.WEEKS) ||
    (endDateAfterTerm === VALUE_MONTHS &&
      endDateAfterNumber > MAX_STOP_AFTER_TERM_VALUES.MONTHS) ||
    (endDateAfterTerm === VALUE_OCCURRENCES &&
      endDateAfterNumber > MAX_STOP_AFTER_TERM_VALUES.OCCURRENCES)

  const frequencyValidationError =
    (repeatBy === VALUE_DAYS && frequency > MAX_STOP_AFTER_TERM_VALUES.DAYS) ||
    (repeatBy === VALUE_WEEKS &&
      frequency > MAX_STOP_AFTER_TERM_VALUES.WEEKS) ||
    (repeatBy === VALUE_MONTHS && frequency > MAX_STOP_AFTER_TERM_VALUES.MONTHS)

  const dueDateTimeValidationErrorCondition =
    dueDate === VALUE_CUSTOM && (!dueDateTime || !dueDateTimeIsValid)
  const startTimeValidationErrorCondition = !startTime
  const dueDateValidationErrorCondition =
    repeat !== VALUE_NEVER && dueDate !== VALUE_CUSTOM
  const frequencyValidationErrorCondition =
    repeat === VALUE_CUSTOM && (!frequency || frequency < 1)
  const daysOfWeekValidationErrorCondition =
    repeatBy === VALUE_WEEKS && !daysOfWeek?.length
  const dayOfWeekAndMonthValidationErrorCondition =
    repeatBy === VALUE_MONTHS &&
    !dayOfWeek &&
    !weekOrdinalNumber &&
    !daysOfMonth &&
    !daysOfMonth.length
  const dayOfWeekValidationErrorCondition =
    repeatBy === VALUE_MONTHS &&
    (dayOfWeek ? !weekOrdinalNumber : weekOrdinalNumber)
  const endDateAfterNumbersValidationErrorCondition =
    repeat === VALUE_CUSTOM && endDate === VALUE_AFTER && !endDateAfterNumber

  const someFieldsValidationFailed =
    workflowValidationErrorCondition ||
    sitesValidationErrorCondition ||
    unitsValidationErrorCondition ||
    stopAfterNumberValidationErrorCondition ||
    dueDateTimeValidationErrorCondition ||
    startTimeValidationErrorCondition ||
    dueDateValidationErrorCondition ||
    frequencyValidationErrorCondition ||
    daysOfWeekValidationErrorCondition ||
    dayOfWeekAndMonthValidationErrorCondition ||
    frequencyValidationError ||
    dayOfWeekValidationErrorCondition ||
    endDateAfterNumbersValidationErrorCondition

  const submitAction = () => {
    const updatedFormData = {
      ...formData,
      [REPEAT]: repeat === VALUE_CUSTOM ? REGULAR : repeat,
      [FIELD_DUE_DATE_TIME]: dueDateTime
        ? moment(formData.dueDateTime).format(BACKEND_TIME_FORMAT)
        : null,
      [START_TIME]: startTime
        ? moment(formData.startTime).format(BACKEND_TIME_FORMAT)
        : null,
      [FIELD_START_DATE]: startDate
        ? moment(formData.startDate).format(BACKEND_DATE_FORMAT)
        : null,
      [ASSIGNMENTS]: formatAssignees(formData[ASSIGNMENTS]),
      [FIELD_DUE_DATE_DATE]: dueDateDate
        ? moment(formData.dueDateDate).format(BACKEND_DATE_FORMAT)
        : null,
      [REGULAR_REPEAT_SCHEDULE]: {
        ...formData[REGULAR_REPEAT_SCHEDULE],
        ...(endDateOnDate
          ? {
              [END_DATE_ON_DATE]: moment(
                formData[REGULAR_REPEAT_SCHEDULE][END_DATE_ON_DATE],
              ).format(BACKEND_DATE_FORMAT),
            }
          : {}),
      },
    }

    withPromise(createJob, {
      form: updatedFormData,
      successMessage: formatMessage(messages.successfulCreateJobMessage),
      errorMessage: formatMessage(messages.failedCreateJobMessage),
    }).then(response => {
      if (
        response &&
        !Object.prototype.hasOwnProperty.call(response, 'error')
      ) {
        toggleIsSidebarOpened()
        fetchManagementUITable()
      } else {
        const { errors = [] } = response

        setValidationErrors({
          ...validationErrors,
          ...errors,
        })
      }
    })
  }

  // eslint-disable-next-line max-statements
  const handleSubmit = () => {
    if (someFieldsValidationFailed) {
      const missingFields = {} as Record<string, string>

      if (workflowValidationErrorCondition) {
        missingFields[WORKFLOW] = ''
      }

      if (sitesValidationErrorCondition) {
        missingFields[FIELD_SITE] = ''
      }

      if (unitsValidationErrorCondition) {
        missingFields[FIELD_UNITS] = ''
      }

      if (stopAfterNumberValidationErrorCondition) {
        missingFields[END_DATE_AFTER_NUMBER] = ''
      }

      if (dueDateTimeValidationErrorCondition) {
        missingFields[FIELD_DUE_DATE_TIME] = ''
      }

      if (startTimeValidationErrorCondition) {
        setIsSchedulingCollapseOpened(true)
        missingFields[START_TIME] = ''
      }

      if (dueDateValidationErrorCondition) {
        missingFields[FIELD_DUE_DATE] = ''
      }

      if (frequencyValidationErrorCondition || frequencyValidationError) {
        missingFields[FREQUENCY] = ''
      }

      if (daysOfWeekValidationErrorCondition) {
        missingFields[DAYS_OF_WEEK] = ''
      }

      if (dayOfWeekAndMonthValidationErrorCondition) {
        missingFields[DAY_OF_WEEK] = ''
        missingFields[WEEK_ORDINARY_NUMBER] = ''
        missingFields[DAY_OF_MONTH] = ''
      }

      if (dayOfWeekValidationErrorCondition) {
        missingFields[DAY_OF_WEEK] = ''
        missingFields[WEEK_ORDINARY_NUMBER] = ''
      }

      if (endDateAfterNumbersValidationErrorCondition) {
        missingFields[END_DATE_AFTER_NUMBER] = ''
      }

      setValidationErrors(missingFields)
    } else {
      submitAction()
    }
  }

  const hasMissingRequiredFields = () =>
    Object.prototype.hasOwnProperty.call(validationErrors, WORKFLOW) ||
    Object.prototype.hasOwnProperty.call(
      validationErrors,
      FIELD_DUE_DATE_DATE,
    ) ||
    Object.prototype.hasOwnProperty.call(
      validationErrors,
      END_DATE_AFTER_NUMBER,
    ) ||
    Object.prototype.hasOwnProperty.call(validationErrors, FREQUENCY) ||
    Object.prototype.hasOwnProperty.call(validationErrors, FIELD_SITE) ||
    Object.prototype.hasOwnProperty.call(
      validationErrors,
      FIELD_DUE_DATE_DATE,
    ) ||
    Object.prototype.hasOwnProperty.call(
      validationErrors,
      END_DATE_AFTER_NUMBER,
    )

  const formattedWorkflows = formatList(
    workflows,
    PARAMETER_ID,
    PARAMETER_TITLE,
  )
  const isDisabled = !workflow || !sites.length
  const customValidationErrors = Object.values(validationErrors).filter(Boolean)

  const handleOverlayClick = () => {
    if (isAssignmentSubModalOpened) {
      setIsAssignmentSubModalOpened(false)
    } else {
      toggleIsSidebarOpened()
    }
  }

  return (
    <>
      <PlanNewJobSidebar>
        <StyledModalOverlay
          className={cn({ closed: !isSidebarOpened })}
          onClick={handleOverlayClick}
        />
        <StyledModal
          customWidth="480px"
          opened={isSidebarOpened}
          direction="column"
          isNewHeader={featureFlags.APP_HEADER_V2}>
          <StyledFormSection>
            <StyledModalBoxSection>
              <ModalTitle align="center" justify="space-between">
                <div>{formatMessage(messages.newJob)}</div>
                <IconButton size="small" onClick={toggleIsSidebarOpened}>
                  <CloseIcon height="20px" />
                </IconButton>
              </ModalTitle>
            </StyledModalBoxSection>
            <StyledModalBoxSection>
              <StyledRuleSettings>
                <StyledTitle marginBottom="16">
                  <SchedulingIcon />
                  {formatMessage(messages.workflow)}
                </StyledTitle>
                <StyledCondition align="center" wrap="wrap">
                  <StyledName>
                    {formatMessage(messages.workflow)}
                    <RedAsterisk>*</RedAsterisk>
                  </StyledName>
                  <SelectInput
                    required
                    isValidationFailed={Object.prototype.hasOwnProperty.call(
                      validationErrors,
                      WORKFLOW,
                    )}
                    disabled={isFetching}
                    hasSearchField
                    label={formatMessage(messages.chooseWorkflow)}
                    options={formattedWorkflows}
                    value={workflow}
                    onChange={(value: string) => onChangeWorkflow(value)}
                    isLoading={!formattedWorkflows.length}
                  />
                </StyledCondition>
              </StyledRuleSettings>
            </StyledModalBoxSection>
            <StyledModalBoxSection>
              <StyledRuleSettings>
                <StyledTitle
                  expandable
                  opened={isSiteCollapseOpened}
                  onClick={() =>
                    setIsSiteCollapseOpened(!isSiteCollapseOpened)
                  }>
                  <SiteIcon />
                  {formatMessage(messages.siteAndUnits)}
                </StyledTitle>
              </StyledRuleSettings>
              <Collapse in={isSiteCollapseOpened}>
                <SiteAndUnitsTable
                  isDisabled={isDisabled}
                  selectedSites={formData.sitesAndUnits}
                  onChangeFormValue={onChangeFormValue}
                  validationErrors={validationErrors}
                  setValidationErrors={setValidationErrors}
                />
              </Collapse>
            </StyledModalBoxSection>
            <Assignment
              isOpened={isAssignmentCollapseOpened}
              setIsOpened={setIsAssignmentCollapseOpened}
              isDefaultAssignment={isDefaultAssignment}
              setIsAssignmentSubModalOpened={setIsAssignmentSubModalOpened}
              formData={formData}
            />
            <Scheduling
              isSidebarOpened={isSidebarOpened}
              isSchedulingCollapseOpened={isSchedulingCollapseOpened}
              setIsSchedulingCollapseOpened={setIsSchedulingCollapseOpened}
              onChangeFormValue={onChangeFormValue}
              formData={formData}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
            />
          </StyledFormSection>
          {hasMissingRequiredFields() && (
            <StyledErrorMessageHolder>
              <InfoIcon />
              {formatMessage(messages.fillRequiredFieldsMessage)}
            </StyledErrorMessageHolder>
          )}
          {!!customValidationErrors.length && (
            <StyledErrorMessageHolder>
              <InfoIcon />
              {customValidationErrors.map(error => (
                <div key={error}>{error}</div>
              ))}
            </StyledErrorMessageHolder>
          )}
          <StyledActionSection align="center">
            <StyledButtonHolder>
              <StyledButton
                customStyles={css`
                  margin-right: 10px;
                `}
                disabled={isFetching || isPlanNewJobSaving}
                onClick={handleSubmit}>
                {formatMessage(messages.save)}
              </StyledButton>
              <StyledButton
                type={BUTTON_TYPE_SECONDARY}
                disabled={isFetching}
                onClick={toggleIsSidebarOpened}>
                {formatMessage(messages.cancel)}
              </StyledButton>
            </StyledButtonHolder>
          </StyledActionSection>
        </StyledModal>
      </PlanNewJobSidebar>
      <AssignmentMenu
        isAssignmentSubModalOpened={isAssignmentSubModalOpened}
        setIsAssignmentSubModalOpened={setIsAssignmentSubModalOpened}
        assignmentData={formData[ASSIGNMENTS]}
        formData={formData}
        setFormData={setFormData}
      />
    </>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | boolean
      | List<Site>
      | Map<string, unknown>
      | Map<string, Workflow>
      | List<AssignmentDataItem>
      | Record<string, boolean>
  }) => ({
    workflowList: state.getIn(['myJobs', 'workflowList']) as Map<
      string,
      Workflow
    >,
    stateFormData: state.getIn(['managementUI', 'formData']) as Map<
      string,
      unknown
    >,
    isFetching: state.getIn(['myJobs', 'isFetching']) as boolean,
    siteList: state.getIn(['logbookForm', 'siteList']) as List<Site>,
    defaultAssignments: state.getIn([
      'jobsTriggers',
      'triggerDefaultAssignments',
    ]) as List<AssignmentDataItem>,
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']) as Record<
      string,
      boolean
    >,
    workflowInfo: state.getIn(['myJobs', 'workflowInfo']) as Map<
      string,
      Workflow
    >,
    isPlanNewJobSaving: state.getIn([
      'managementUI',
      'isPlanNewJobSaving',
    ]) as boolean,
  }),
  {
    fetchWorkflowList,
    setFormData,
    fetchSiteList,
    fetchWorkflowInfo,
    fetchDefaultAssignments,
    createJob,
  },
)(injectIntl(PlanNewJob))
